import type { SlashCommand } from '@simple-agent-manager/acp-client';
import { CLIENT_COMMANDS,getAllStaticCommands } from '@simple-agent-manager/acp-client';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { getCachedCommands } from '../lib/api';

/**
 * Merges three tiers of slash commands for the project chat input:
 * 1. Client commands (SAM-defined, always available)
 * 2. Static registry commands (well-known agent commands)
 * 3. Cached commands (previously seen from ACP sessions)
 * 4. Live ACP commands (when a session is active, passed in)
 *
 * Deduplicates by command name with priority: live > cached > static > client.
 *
 * Pass `refreshKey` (e.g. sessionId) to re-fetch cached commands whenever the
 * key changes — ensures newly-persisted commands are picked up by subsequent sessions.
 */
export function useAvailableCommands(
  projectId: string,
  liveCommands?: SlashCommand[],
  refreshKey?: string | null,
): { commands: SlashCommand[]; isLoading: boolean; refetch: () => void; persistCommands: (agentType: string, cmds: SlashCommand[]) => void } {
  const [cachedCommands, setCachedCommands] = useState<SlashCommand[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchCachedCommands = useCallback((signal?: { cancelled: boolean }) => {
    setIsLoading(true);
    getCachedCommands(projectId)
      .then((result) => {
        if (signal?.cancelled) return;
        setCachedCommands(
          result.commands.map((cmd) => ({
            name: cmd.name,
            description: cmd.description,
            source: 'cached' as const,
          })),
        );
      })
      .catch(() => {
        // Non-fatal — static commands are still available
      })
      .finally(() => {
        if (!signal?.cancelled) setIsLoading(false);
      });
  }, [projectId]);

  // Fetch cached commands on mount, when projectId changes, or when refreshKey changes
  useEffect(() => {
    const signal = { cancelled: false };
    fetchCachedCommands(signal);
    return () => { signal.cancelled = true; };
  }, [fetchCachedCommands, refreshKey]);

  // Imperative refetch for callers (e.g. after persisting new commands)
  const refetch = useCallback(() => fetchCachedCommands(), [fetchCachedCommands]);

  // Allow callers to trigger a cache persist (fire-and-forget)
  const persistCommands = useCallback(
    (agentType: string, cmds: SlashCommand[]) => {
      import('../lib/api').then(({ saveCachedCommands }) => {
        saveCachedCommands(
          projectId,
          agentType,
          cmds.map((c) => ({ name: c.name, description: c.description })),
        ).catch(() => { /* best-effort */ });
      });
    },
    [projectId],
  );

  // Merge all sources with dedup (live > cached > static > client)
  const commands = useMemo(() => {
    const seen = new Map<string, SlashCommand>();

    // Lowest priority first — higher priority overwrites
    for (const cmd of CLIENT_COMMANDS) seen.set(cmd.name, cmd);
    for (const cmd of getAllStaticCommands()) seen.set(cmd.name, cmd);
    for (const cmd of cachedCommands) seen.set(cmd.name, cmd);
    if (liveCommands) {
      for (const cmd of liveCommands) seen.set(cmd.name, cmd);
    }

    return Array.from(seen.values());
  }, [cachedCommands, liveCommands]);

  return { commands, isLoading, refetch, persistCommands };
}
